import React, { useEffect, useState } from "react";
import TopNavbar from "../../components/Gnavbar";
import BottomNavbar from "../../components/BottomNavbar";
import { useNavigate } from "react-router-dom";
import {
  deleteRequest,
  getRequest,
  postWithFormData,
} from "../../helpers/api.helper";
import Loader from "../../components/Loader";
import { FaArrowLeft, FaTrashAlt } from "react-icons/fa";
import { UPLOAD_URL } from "../../utils/constants";
import { toast } from "react-toastify";
import heic2any from "heic2any";
import imageCompression from "browser-image-compression";
import { useLanguage } from "../../context/LanguageContext";

const Sliderimages = () => {
  const navigate = useNavigate();
const {t} = useLanguage()
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [bannerImages, setBannerImages] = useState<
    { id: number; imageUrl: string }[]
  >([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // State to control modal visibility
  const [imageIdToDelete, setImageIdToDelete] = useState<number | null>(null); // State to store the image ID to delete
  const canSaveImage = Boolean(image && preview && !uploading);

  const fetchBannerImages = async () => {
    try {
      const response = await getRequest(`/bureau/banners`);
      // Check if the response contains the expected data structure
      if (response.success) {
        setBannerImages(response.data); // Assuming response.data.data contains the images array
      } else {
        setBannerImages([]);
      }
    } catch (error) {
      console.error("Error fetching banner images:", error);
      setBannerImages([]);
    }
  };

  useEffect(() => {
    fetchBannerImages();
  }, []);

  const handleImageChange = async (e: any) => {
    let file = e.target.files[0];
    if (!file) return;
    setImage(null);
    setPreview(null);

    try {
      // 1️⃣ Convert HEIC → JPG
      if (file.type === "image/heic" || file.type === "image/heif") {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.9,
        });

        file = new File([convertedBlob as Blob], "image.jpg", {
          type: "image/jpeg",
        });
      }

      // 2️⃣ Compress image (THIS IS THE KEY FIX)
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1, // 👈 keep under 1MB
        maxWidthOrHeight: 1600, // 👈 reduce resolution
        useWebWorker: true,
      });

      setImage(compressedFile);
      setPreview(URL.createObjectURL(compressedFile));
    } catch (err) {
      console.error(err);
      toast.error("Unable to process image. Please try another one.");
    }
  };

  const handleSubmit = async () => {
    if (!image || !preview) {
      toast.error("Please select an image");
      return;
    }
    setUploading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("image", image);

      const response = await postWithFormData(
        `/bureau/banners`,
        formDataToSend
      );
      if (response.success) {
        toast.success("Image uploaded successfully!");
        setPreview(null);
        setImage(null);
        setUploading(false);

        setBannerImages((prev) => [...prev, response.data]);
      } else {
        toast.error(response.message);
        setUploading(false);
      }
    } catch (error: any) {
      console.error("Upload error:", error);

      // Axios-style network error
      if (!error.response) {
        if (!navigator.onLine) {
          toast.error("No internet connection. Please check your network.");
          return;
        }

        toast.error(
          "Upload failed due to network or server restriction. " +
            "Please try a smaller image or check your connection."
        );
        return;
      }

      // Server responded with error
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
        return;
      }

      toast.error("Unexpected error occurred while uploading image.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteModal(false); // Close modal
    try {
      const response = await deleteRequest(
        `/bureau/banners/${imageIdToDelete}`,
        {}
      );
      if (response.success) {
        toast.success("Image deleted successfully!");
        setBannerImages(
          bannerImages.filter((image) => image.id !== imageIdToDelete)
        ); // Update state by removing the deleted image
      } else {
        toast.error(response.message || "Failed to delete image.");
      }
    } catch (error: any) {
      console.error("Error deleting image:", error);
      toast.error(error.message || "There was an error deleting the image.");
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <TopNavbar />
      <>
        {uploading ? (
          <Loader />
        ) : (
          <div className="h-auto bg-gray-100 py-10 px-4 mb-20">
            {/* Back button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
            >
              <FaArrowLeft className="mr-2" /> {t("Back")}
            </button>

            <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg h-auto">
              <h2 className="text-2xl font-semibold text-center text-yellow-600 mb-8">
                {t("Upload / Manage Slider Banners")}
              </h2>
              {/* Image Upload Field */}
              <div>
                <label
                  htmlFor="image"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("Upload New Image")}
                </label>
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="mt-2 w-full px-4 py-2 border border-black rounded-md focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>

              {/* New Image Preview */}
              {preview && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700">
                    {t("New Image Preview:")}
                  </h3>
                  <img
                    src={preview}
                    alt="Preview"
                    className="mt-2 w-32 h-32 object-cover rounded-md"
                  />
                </div>
              )}

              {/* Submit Button */}
              <div>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSaveImage}
                  className="w-full bg-yellow-500 text-white py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                >
                  {uploading ? "Uploading..." : "Save Image"}
                </button>
              </div>

              {/* Display All Banner Images as Thumbnails */}
              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-700 mb-4">
                  {t("Existing Slider Images")}
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {bannerImages.length > 0 ? (
                    bannerImages.map((image, index) => {
                      const imageUrl = `${UPLOAD_URL}/bureaus/banners/${image.imageUrl}`;

                      return (
                        <div key={index} className="relative">
                          <img
                            src={imageUrl}
                            alt={`Banner ${index + 1}`}
                            className="w-24 h-24 object-cover rounded-md cursor-pointer"
                          />
                          {/* Delete Icon */}
                          <button
                            onClick={() => {
                              setImageIdToDelete(image.id);
                              setShowDeleteModal(true);
                            }}
                            className="absolute top-0 right-0 text-white bg-red-600 p-1 rounded-full shadow-md hover:bg-red-700"
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <p>{t("No images found.")}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal for delete confirmation */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">
                {t("Are you sure you want to delete this image?")}
              </h3>
              <div className="flex justify-between">
                <button
                  onClick={handleDelete}
                  className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700"
                >
                  {t("Yes, Delete")}
                </button>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="bg-gray-300 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  {t("Cancel")}
                </button>
              </div>
            </div>
          </div>
        )}
      </>

      <BottomNavbar />
    </div>
  );
};

export default Sliderimages;
